import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Download, ExternalLink, LoaderCircle, Mail, Search } from "lucide-react";

const LEAD_STATUSES = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "archived", label: "Archived" },
];

const FAILURE_ALERT_DISMISSED_KEY = "lovequetee-failure-alert-dismissed";

const DELIVERY_LABELS = {
  contact_acknowledgement: "Contact acknowledgement",
  contact_notification: "Studio contact notification",
  curator_acknowledgement: "Curator acknowledgement",
  curator_notification: "Studio curator notification",
  press_acknowledgement: "Press acknowledgement",
  press_notification: "Studio press notification",
  acquisition_acknowledgement: "Acquisition acknowledgement",
  acquisition_notification: "Studio acquisition notification",
  newsletter_acknowledgement: "Newsletter acknowledgement",
  newsletter_notification: "Studio newsletter notification",
  purchase_receipt: "Purchase receipt",
  shipment_update: "Shipment update",
  delivery_failure_alert: "Delivery failure alert",
  transactional: "Transactional email",
};

function formatLeadDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatDeliveryDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function leadStatus(value) {
  return LEAD_STATUSES.some((status) => status.value === value) ? value : "new";
}

function deliveryLabel(value) {
  return DELIVERY_LABELS[value] || value || "Email delivery";
}

function DeliveryEntry({ delivery, relatedLabel, relatedLeadId, testPrefix, onReview, onAddNote, onShowFailed, notePromptKey, reviewSavingKey, reviewErrorKey, reviewError }) {
  const status = delivery.delivery_status || "failed";
  return (
    <div className={`studio-email-delivery studio-email-delivery-${status}`} data-testid={`${testPrefix}-${delivery.id}`}>
      <div className="studio-email-delivery-main">
        <strong data-testid={`${testPrefix}-type-${delivery.id}`}>{deliveryLabel(delivery.email_type)}</strong>
        <a href={`mailto:${delivery.recipient}`} data-testid={`${testPrefix}-recipient-${delivery.id}`}>{delivery.recipient}</a>
      </div>
      <div className="studio-email-delivery-meta">
        <span className={`studio-email-delivery-status studio-email-delivery-status-${status}`} data-testid={`${testPrefix}-status-${delivery.id}`}>{status === "sent" ? "Sent" : "Failed"}</span>
        <time dateTime={delivery.sent_at} data-testid={`${testPrefix}-time-${delivery.id}`}>{formatDeliveryDate(delivery.sent_at)}</time>
        {delivery.provider_id && <code title={delivery.provider_id} data-testid={`${testPrefix}-provider-${delivery.id}`}>{delivery.provider_id}</code>}
        {relatedLabel && <span data-testid={`${testPrefix}-lead-reference-${delivery.id}`}>Conversation / {relatedLabel}</span>}
        {delivery.related_order_reference && <span data-testid={`${testPrefix}-order-reference-${delivery.id}`}>Reference / {delivery.related_order_reference}</span>}
        {status === "failed" && (
          <div className="studio-email-delivery-review">
            {delivery.reviewed ? (
              <div className="studio-email-delivery-reviewed-wrap" data-testid={`${testPrefix}-reviewed-followup-${delivery.id}`}>
                <span className="studio-email-delivery-reviewed" data-testid={`${testPrefix}-reviewed-${delivery.id}`}>Reviewed</span>
                <span className="studio-email-delivery-followup-copy" data-testid={`${testPrefix}-followup-copy-${delivery.id}`}>Ready for a quiet next step.</span>
                <div className="studio-email-delivery-followup-actions">
                  <button type="button" onClick={() => onAddNote(delivery)} data-testid={`${testPrefix}-add-note-${delivery.id}`}>Add a private note</button>
                  <button type="button" onClick={onShowFailed} data-testid={`${testPrefix}-view-failed-${delivery.id}`}>View failed</button>
                </div>
                {notePromptKey === delivery.id && <span className="studio-email-delivery-followup-note" data-testid={`${testPrefix}-followup-note-${delivery.id}`}>{relatedLeadId ? "Private note field is below this conversation." : "No related conversation note is attached."}</span>}
              </div>
            ) : <button type="button" onClick={() => onReview(delivery)} disabled={reviewSavingKey === delivery.id} data-testid={`${testPrefix}-mark-reviewed-${delivery.id}`}>{reviewSavingKey === delivery.id ? "Saving…" : "Mark reviewed"}</button>}
            {reviewErrorKey === delivery.id && <span className="studio-email-delivery-review-error" role="alert" data-testid={`${testPrefix}-review-error-${delivery.id}`}>{reviewError}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function LeadDeliveryTrail({ lead, deliveries, onReview, onAddNote, onShowFailed, notePromptKey, reviewSavingKey, reviewErrorKey, reviewError }) {
  return (
    <div className="studio-contact-lead-deliveries" data-testid={`studio-contact-lead-deliveries-${lead.id}`}>
      <div className="studio-contact-lead-deliveries-heading">
        <span className="studio-lead-label">Email trail</span>
        <span data-testid={`studio-contact-lead-deliveries-count-${lead.id}`}>{deliveries.length} {deliveries.length === 1 ? "delivery" : "deliveries"}</span>
      </div>
      {deliveries.length > 0 ? (
        <div className="studio-email-delivery-list">
          {deliveries.map((delivery) => <DeliveryEntry key={delivery.id} delivery={delivery} relatedLabel={lead.name} relatedLeadId={lead.id} testPrefix="studio-contact-lead-delivery" onReview={onReview} onAddNote={onAddNote} onShowFailed={onShowFailed} notePromptKey={notePromptKey} reviewSavingKey={reviewSavingKey} reviewErrorKey={reviewErrorKey} reviewError={reviewError} />)}
        </div>
      ) : <span className="studio-contact-lead-deliveries-empty">No email delivery recorded yet.</span>}
    </div>
  );
}

export function StudioContactLeads({ api }) {
  const [leads, setLeads] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [total, setTotal] = useState(0);
  const [deliveryTotal, setDeliveryTotal] = useState(0);
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState("all");
  const [failedFirst, setFailedFirst] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [noteDrafts, setNoteDrafts] = useState({});
  const [statusSavingKey, setStatusSavingKey] = useState("");
  const [statusSavedKey, setStatusSavedKey] = useState("");
  const [statusErrorKey, setStatusErrorKey] = useState("");
  const [statusError, setStatusError] = useState("");
  const [noteSavingKey, setNoteSavingKey] = useState("");
  const [noteSavedKey, setNoteSavedKey] = useState("");
  const [noteErrorKey, setNoteErrorKey] = useState("");
  const [noteError, setNoteError] = useState("");
  const [failureAlertDismissed, setFailureAlertDismissed] = useState(() => window.sessionStorage.getItem(FAILURE_ALERT_DISMISSED_KEY) === "1");
  const [reviewSavingKey, setReviewSavingKey] = useState("");
  const [reviewErrorKey, setReviewErrorKey] = useState("");
  const [reviewError, setReviewError] = useState("");
  const [notePromptKey, setNotePromptKey] = useState("");
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupSaved, setBackupSaved] = useState(false);
  const [backupError, setBackupError] = useState("");
  const [analyticsNote, setAnalyticsNote] = useState("");
  const [analyticsNoteDraft, setAnalyticsNoteDraft] = useState("");
  const [analyticsNoteEditing, setAnalyticsNoteEditing] = useState(false);
  const [analyticsNoteLoading, setAnalyticsNoteLoading] = useState(true);
  const [analyticsNoteSaving, setAnalyticsNoteSaving] = useState(false);
  const [analyticsNoteSaved, setAnalyticsNoteSaved] = useState(false);
  const [analyticsNoteError, setAnalyticsNoteError] = useState("");

  const loadAnalyticsNote = useCallback(async () => {
    setAnalyticsNoteLoading(true);
    setAnalyticsNoteError("");
    try {
      const response = await fetch(`${api}/api/studio/analytics-note`, { credentials: "include" });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "The private analytics note could not be loaded");
      const note = payload.analytics_note?.note || "";
      setAnalyticsNote(note);
      setAnalyticsNoteDraft(note);
    } catch (loadError) {
      setAnalyticsNoteError(loadError instanceof Error ? loadError.message : "The private analytics note could not be loaded");
    } finally {
      setAnalyticsNoteLoading(false);
    }
  }, [api]);

  const loadLeads = useCallback(async (query = "") => {
    setLoading(true);
    setError("");
    try {
      const params = query ? `?search=${encodeURIComponent(query)}` : "";
      const response = await fetch(`${api}/api/studio/contact-leads${params}`, { credentials: "include" });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "Contact leads could not be loaded");
      if (!Array.isArray(payload.leads) || !Number.isInteger(payload.total)) throw new Error("Contact leads returned an invalid list");
      const incomingDeliveries = Array.isArray(payload.deliveries) ? payload.deliveries : [];
      setLeads(payload.leads);
      setDeliveries(incomingDeliveries);
      setTotal(payload.total);
      setDeliveryTotal(Number.isInteger(payload.delivery_total) ? payload.delivery_total : incomingDeliveries.length);
      setNoteDrafts((current) => payload.leads.reduce((next, lead) => ({ ...next, [lead.id]: lead.follow_up_note || "" }), current));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Contact leads could not be loaded");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadLeads();
    loadAnalyticsNote();
  }, [loadLeads, loadAnalyticsNote]);

  const submitSearch = (event) => {
    event.preventDefault();
    const query = searchInput.trim();
    setActiveSearch(query);
    loadLeads(query);
  };

  const clearSearch = () => {
    setSearchInput("");
    setActiveSearch("");
    loadLeads();
  };

  const updateStatus = async (lead, nextStatus) => {
    const leadKey = lead.id;
    setStatusSavingKey(leadKey);
    setStatusSavedKey("");
    setStatusErrorKey("");
    setStatusError("");
    try {
      const response = await fetch(`${api}/api/studio/contact-leads/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ lead_id: lead.id, status: nextStatus }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "The contact lead status could not be saved");
      if (!payload.lead) throw new Error("Contact lead status returned an invalid record");
      setLeads((current) => current.map((row) => row.id === lead.id ? payload.lead : row));
      setStatusSavedKey(leadKey);
      window.setTimeout(() => setStatusSavedKey((current) => current === leadKey ? "" : current), 1800);
    } catch (saveError) {
      setStatusErrorKey(leadKey);
      setStatusError(saveError instanceof Error ? saveError.message : "The contact lead status could not be saved");
    } finally {
      setStatusSavingKey("");
    }
  };

  const updateNote = async (lead, draft) => {
    const note = draft.trim();
    if (note === (lead.follow_up_note || "")) return;
    const leadKey = lead.id;
    setNoteSavingKey(leadKey);
    setNoteSavedKey("");
    setNoteErrorKey("");
    setNoteError("");
    try {
      const response = await fetch(`${api}/api/studio/contact-leads/note`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ lead_id: lead.id, follow_up_note: note }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "The private follow-up note could not be saved");
      if (!payload.lead) throw new Error("Private follow-up note returned an invalid record");
      setLeads((current) => current.map((row) => row.id === lead.id ? payload.lead : row));
      setNoteDrafts((current) => ({ ...current, [lead.id]: payload.lead.follow_up_note || "" }));
      setNoteSavedKey(leadKey);
      window.setTimeout(() => setNoteSavedKey((current) => current === leadKey ? "" : current), 1800);
    } catch (saveError) {
      setNoteErrorKey(leadKey);
      setNoteError(saveError instanceof Error ? saveError.message : "The private follow-up note could not be saved");
    } finally {
      setNoteSavingKey("");
    }
  };

  const saveAnalyticsNote = async (nextNote = analyticsNoteDraft) => {
    const note = nextNote.trim();
    setAnalyticsNoteSaving(true);
    setAnalyticsNoteSaved(false);
    setAnalyticsNoteError("");
    try {
      const response = await fetch(`${api}/api/studio/analytics-note`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ note }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "The private analytics note could not be saved");
      const savedNote = payload.analytics_note?.note || "";
      setAnalyticsNote(savedNote);
      setAnalyticsNoteDraft(savedNote);
      setAnalyticsNoteEditing(false);
      setAnalyticsNoteSaved(true);
      window.setTimeout(() => setAnalyticsNoteSaved(false), 1800);
    } catch (saveError) {
      setAnalyticsNoteError(saveError instanceof Error ? saveError.message : "The private analytics note could not be saved");
    } finally {
      setAnalyticsNoteSaving(false);
    }
  };

  const beginAnalyticsNoteEdit = () => {
    setAnalyticsNoteDraft(analyticsNote);
    setAnalyticsNoteEditing(true);
    setAnalyticsNoteError("");
  };

  const clearAnalyticsNote = () => {
    setAnalyticsNoteDraft("");
    saveAnalyticsNote("");
  };

  const downloadLedgerBackup = async () => {
    setBackupLoading(true);
    setBackupSaved(false);
    setBackupError("");
    try {
      const response = await fetch(`${api}/api/studio/ledgers/backup`, { credentials: "include" });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.detail || "The studio ledger backup could not be downloaded");
      }
      const blob = await response.blob();
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `love-quetee-studio-ledger-backup-${timestamp}.json`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
      setBackupSaved(true);
      window.setTimeout(() => setBackupSaved(false), 1800);
    } catch (backupDownloadError) {
      setBackupError(backupDownloadError instanceof Error ? backupDownloadError.message : "The studio ledger backup could not be downloaded");
    } finally {
      setBackupLoading(false);
    }
  };

  const promptPrivateNote = (delivery) => {
    setNotePromptKey(delivery.id);
    if (delivery.related_lead_id) {
      document.getElementById(`studio-contact-lead-note-input-${delivery.related_lead_id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const reviewDelivery = async (delivery) => {
    const deliveryKey = delivery.id;
    setReviewSavingKey(deliveryKey);
    setReviewErrorKey("");
    setReviewError("");
    try {
      const response = await fetch(`${api}/api/studio/contact-leads/delivery-review`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ delivery_id: deliveryKey, reviewed: true }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "The email delivery review could not be saved");
      if (!payload.delivery) throw new Error("Email delivery review returned an invalid record");
      setDeliveries((current) => current.map((row) => row.id === deliveryKey ? payload.delivery : row));
    } catch (saveError) {
      setReviewErrorKey(deliveryKey);
      setReviewError(saveError instanceof Error ? saveError.message : "The email delivery review could not be saved");
    } finally {
      setReviewSavingKey("");
    }
  };

  const filteredDeliveries = deliveries
    .filter((delivery) => deliveryStatusFilter === "all" || delivery.delivery_status === deliveryStatusFilter)
    .sort((left, right) => {
      if (failedFirst && left.delivery_status !== right.delivery_status) return left.delivery_status === "failed" ? -1 : 1;
      return new Date(right.sent_at).getTime() - new Date(left.sent_at).getTime();
    });
  const unreviewedFailures = deliveries.filter((delivery) => delivery.delivery_status === "failed" && !delivery.reviewed);
  const unlinkedDeliveries = filteredDeliveries.filter((delivery) => !delivery.related_lead_id);
  const clearDeliveryFilters = () => {
    setDeliveryStatusFilter("all");
    setFailedFirst(false);
  };
  const showFailedDeliveries = () => {
    setDeliveryStatusFilter("failed");
    setFailedFirst(false);
  };
  const dismissFailureAlert = () => {
    window.sessionStorage.setItem(FAILURE_ALERT_DISMISSED_KEY, "1");
    setFailureAlertDismissed(true);
  };

  return (
    <section className="studio-contact-leads" data-testid="studio-contact-leads">
      <div className="studio-contact-leads-header" data-testid="studio-contact-leads-header">
        <div>
          <p className="section-kicker">Studio correspondence / contact record</p>
          <h2 data-testid="studio-contact-leads-title">Keep the good conversations<br /><em>in reach.</em></h2>
          <p className="studio-contact-leads-intro" data-testid="studio-contact-leads-intro">Every Contact enquiry stays here, with curator and press notes clearly marked for the next quiet follow-up.</p>
        </div>
        <div className="studio-contact-leads-header-meta">
          <a className="studio-ledger-backup-button studio-ledger-dashboard-link" href={process.env.REACT_APP_PLAUSIBLE_DASHBOARD_URL} target="_blank" rel="noopener noreferrer" data-testid="studio-plausible-dashboard-link"><ExternalLink size={14} strokeWidth={1.4} /> View analytics</a>
          <div className="studio-analytics-note" data-testid="studio-analytics-note">
            <span className="studio-analytics-note-label">Private campaign note</span>
            {analyticsNoteLoading ? <span className="studio-analytics-note-loading" data-testid="studio-analytics-note-loading">Loading note…</span> : analyticsNoteEditing || !analyticsNote ? (
              <div className="studio-analytics-note-editor">
                <textarea value={analyticsNoteDraft} onChange={(event) => setAnalyticsNoteDraft(event.target.value)} maxLength={240} rows={2} placeholder="What should stay in view as traffic grows?" aria-label="Private campaign note" data-testid="studio-analytics-note-input" />
                <div className="studio-analytics-note-actions">
                  <span className="studio-analytics-note-count" data-testid="studio-analytics-note-count">{analyticsNoteDraft.length} / 240</span>
                  <button type="button" onClick={() => saveAnalyticsNote()} disabled={analyticsNoteSaving || !analyticsNoteDraft.trim()} data-testid="studio-analytics-note-save-button">{analyticsNoteSaving ? "Saving…" : "Save"}</button>
                  {analyticsNote && <button type="button" onClick={() => { setAnalyticsNoteDraft(analyticsNote); setAnalyticsNoteEditing(false); setAnalyticsNoteError(""); }} disabled={analyticsNoteSaving} data-testid="studio-analytics-note-cancel-button">Cancel</button>}
                </div>
              </div>
            ) : (
              <>
                <p className="studio-analytics-note-value" data-testid="studio-analytics-note-value">{analyticsNote}</p>
                <div className="studio-analytics-note-actions">
                  <button type="button" onClick={beginAnalyticsNoteEdit} data-testid="studio-analytics-note-edit-button">Edit</button>
                  <button type="button" onClick={clearAnalyticsNote} disabled={analyticsNoteSaving} data-testid="studio-analytics-note-clear-button">{analyticsNoteSaving ? "Clearing…" : "Clear"}</button>
                </div>
              </>
            )}
            {analyticsNoteSaved && <span className="studio-analytics-note-saved" data-testid="studio-analytics-note-saved">Saved</span>}
            {analyticsNoteError && <span className="studio-analytics-note-error" role="alert" data-testid="studio-analytics-note-error">{analyticsNoteError}</span>}
          </div>
          <span className="studio-contact-leads-count" data-testid="studio-contact-leads-count">{total} {total === 1 ? "conversation" : "conversations"}</span>
          <button className="studio-ledger-backup-button" type="button" onClick={downloadLedgerBackup} disabled={backupLoading} aria-busy={backupLoading} data-testid="studio-ledger-backup-button">
            <Download size={14} strokeWidth={1.4} /> {backupLoading ? "Preparing backup…" : "Download ledger backup"}
          </button>
          {backupSaved && <span className="studio-ledger-backup-status" data-testid="studio-ledger-backup-saved">Backup downloaded</span>}
          {backupError && <span className="studio-ledger-backup-error" role="alert" data-testid="studio-ledger-backup-error">{backupError}</span>}
        </div>
      </div>
      {!loading && !error && unreviewedFailures.length > 0 && !failureAlertDismissed && (
        <div className="studio-email-failure-alert" role="alert" data-testid="studio-email-failure-alert">
          <div className="studio-email-failure-alert-copy">
            <span className="studio-email-failure-alert-kicker" data-testid="studio-email-failure-alert-kicker">Delivery attention</span>
            <strong data-testid="studio-email-failure-alert-count">{unreviewedFailures.length} failed {unreviewedFailures.length === 1 ? "delivery needs" : "deliveries need"} review.</strong>
            <p data-testid="studio-email-failure-alert-message">A managed email did not reach its destination. Review the ledger and decide on the next quiet follow-up.</p>
          </div>
          <div className="studio-email-failure-alert-actions">
            <button type="button" onClick={showFailedDeliveries} data-testid="studio-email-failure-alert-show-button">Show failed</button>
            <button type="button" onClick={dismissFailureAlert} data-testid="studio-email-failure-alert-dismiss-button">Dismiss for this session</button>
          </div>
        </div>
      )}
      <form className="studio-contact-leads-search" onSubmit={submitSearch} data-testid="studio-contact-leads-search-form">
        <label>
          <span className="studio-lead-label">Find a conversation</span>
          <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Name, email, role, or note" aria-label="Search contact leads" data-testid="studio-contact-leads-search-input" />
        </label>
        <button className="studio-contact-leads-search-button" type="submit" data-testid="studio-contact-leads-search-button"><Search size={15} strokeWidth={1.4} /> Search</button>
        {activeSearch && <button className="studio-contact-leads-clear-button" type="button" onClick={clearSearch} data-testid="studio-contact-leads-clear-search">Clear search</button>}
      </form>
      <div className="studio-email-delivery-filters" data-testid="studio-email-delivery-filters">
        <label className="studio-email-delivery-filter-field">
          <span className="studio-lead-label">Delivery status</span>
          <select value={deliveryStatusFilter} onChange={(event) => setDeliveryStatusFilter(event.target.value)} aria-label="Filter email deliveries by status" data-testid="studio-email-delivery-status-filter">
            <option value="all">All</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
          </select>
        </label>
        <label className="studio-email-delivery-sort-toggle">
          <input type="checkbox" checked={failedFirst} onChange={(event) => setFailedFirst(event.target.checked)} data-testid="studio-email-delivery-failed-first-toggle" />
          <span>Failed first</span>
        </label>
        <span className="studio-email-delivery-filter-count" data-testid="studio-email-delivery-filter-count">{filteredDeliveries.length} of {deliveryTotal} deliveries</span>
        {(deliveryStatusFilter !== "all" || failedFirst) && <button className="studio-email-delivery-filter-clear" type="button" onClick={clearDeliveryFilters} data-testid="studio-email-delivery-filter-clear">Clear delivery filters</button>}
      </div>
      {loading && <div className="studio-contact-leads-loading" data-testid="studio-contact-leads-loading"><LoaderCircle className="checkout-spinner" size={17} /> Consulting contact archive…</div>}
      {!loading && error && (
        <div className="studio-contact-leads-error" role="alert" data-testid="studio-contact-leads-error">
          <span>{error}</span>
          <button type="button" onClick={() => loadLeads(activeSearch)} data-testid="studio-contact-leads-retry-button">Retry contact archive</button>
        </div>
      )}
      {!loading && !error && !leads.length && (
        <div className="studio-contact-leads-empty" data-testid="studio-contact-leads-empty">
          <Mail size={20} strokeWidth={1.2} />
          <strong>{activeSearch ? "Nothing matched this search." : "No conversations have arrived yet."}</strong>
          <span>{activeSearch ? "Try a name, role, or a shorter phrase." : "New studio enquiries will appear here after the public Contact form is sent."}</span>
        </div>
      )}
      {!loading && !error && leads.length > 0 && (
        <div className="studio-contact-leads-list" data-testid="studio-contact-leads-list">
          {leads.map((lead) => {
            const status = leadStatus(lead.status);
            const noteDraft = noteDrafts[lead.id] ?? lead.follow_up_note ?? "";
            const statusSaving = statusSavingKey === lead.id;
            const noteSaving = noteSavingKey === lead.id;
            const leadDeliveries = filteredDeliveries.filter((delivery) => delivery.related_lead_id === lead.id);
            return (
              <article className="studio-contact-lead" key={lead.id} data-testid={`studio-contact-lead-${lead.id}`}>
                <div className="studio-contact-lead-topline">
                  <div className="studio-contact-lead-person">
                    <span className={`studio-contact-lead-source studio-contact-lead-source-${lead.source || "contact"}`} data-testid={`studio-contact-lead-source-${lead.id}`}>{lead.source_label || "Contact"}</span>
                    <h3 data-testid={`studio-contact-lead-name-${lead.id}`}>{lead.name}</h3>
                    <a href={`mailto:${lead.email}`} data-testid={`studio-contact-lead-email-${lead.id}`}>{lead.email}</a>
                  </div>
                  <div className="studio-contact-lead-date">
                    <span className="studio-lead-label">Received</span>
                    <time dateTime={lead.created_at} data-testid={`studio-contact-lead-date-${lead.id}`}>{formatLeadDate(lead.created_at)}</time>
                  </div>
                </div>
                <div className="studio-contact-lead-role" data-testid={`studio-contact-lead-role-${lead.id}`}>Writing as / {lead.role}</div>
                {lead.work_reference && <div className="studio-contact-lead-work" data-testid={`studio-contact-lead-work-${lead.id}`}>Work / {lead.work_reference}</div>}
                <p className="studio-contact-lead-message" data-testid={`studio-contact-lead-message-${lead.id}`}>{lead.message}</p>
                <LeadDeliveryTrail lead={lead} deliveries={leadDeliveries} onReview={reviewDelivery} onAddNote={promptPrivateNote} onShowFailed={showFailedDeliveries} notePromptKey={notePromptKey} reviewSavingKey={reviewSavingKey} reviewErrorKey={reviewErrorKey} reviewError={reviewError} />
                <div className="studio-contact-lead-followup">
                  <label className="studio-contact-lead-note-field">
                    <span className="studio-lead-label">Private follow-up note</span>
                    <textarea value={noteDraft} maxLength={240} rows="2" onChange={(event) => setNoteDrafts((current) => ({ ...current, [lead.id]: event.target.value }))} onBlur={() => updateNote(lead, noteDraft)} placeholder="Add a quiet note for the next reply" aria-label={`Private follow-up note for ${lead.name}`} data-testid={`studio-contact-lead-note-input-${lead.id}`} />
                  </label>
                  <span className="studio-contact-lead-note-count" data-testid={`studio-contact-lead-note-count-${lead.id}`}>{noteDraft.length}/240</span>
                  {noteSaving && <span className="studio-contact-lead-feedback" data-testid={`studio-contact-lead-note-saving-${lead.id}`}>Saving…</span>}
                  {noteSavedKey === lead.id && <span className="studio-contact-lead-feedback" data-testid={`studio-contact-lead-note-saved-${lead.id}`}><CheckCircle2 size={13} /> Saved</span>}
                  {noteErrorKey === lead.id && <span className="studio-contact-lead-error" role="alert" data-testid={`studio-contact-lead-note-error-${lead.id}`}>{noteError}</span>}
                </div>
                <div className="studio-contact-lead-footer">
                  <label className="studio-contact-lead-status-field">
                    <span className="studio-lead-label">Follow-up status</span>
                    <select value={status} disabled={statusSaving} onChange={(event) => updateStatus(lead, event.target.value)} aria-label={`Follow-up status for ${lead.name}`} data-testid={`studio-contact-lead-status-${lead.id}`}>
                      {LEAD_STATUSES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                  </label>
                  <span className="studio-contact-lead-status-feedback" aria-live="polite">
                    {statusSaving && <span data-testid={`studio-contact-lead-status-saving-${lead.id}`}>Saving…</span>}
                    {statusSavedKey === lead.id && <span data-testid={`studio-contact-lead-status-saved-${lead.id}`}><CheckCircle2 size={13} /> Saved</span>}
                    {statusErrorKey === lead.id && <span className="studio-contact-lead-error" role="alert" data-testid={`studio-contact-lead-status-error-${lead.id}`}>{statusError}</span>}
                  </span>
                  {lead.note_updated_at && <span className="studio-contact-lead-updated" data-testid={`studio-contact-lead-updated-${lead.id}`}>Note updated {formatLeadDate(lead.note_updated_at)}</span>}
                </div>
              </article>
            );
          })}
        </div>
      )}
      {!loading && !error && deliveries.length > 0 && filteredDeliveries.length === 0 && (
        <div className="studio-email-delivery-filter-empty" data-testid="studio-email-delivery-filter-empty">
          <span>No deliveries match these filters.</span>
          <button type="button" onClick={clearDeliveryFilters} data-testid="studio-email-delivery-filter-empty-clear">Clear delivery filters</button>
        </div>
      )}
      {!loading && !error && unlinkedDeliveries.length > 0 && (
        <section className="studio-email-delivery-ledger" data-testid="studio-email-delivery-ledger">
          <div className="studio-email-delivery-ledger-header">
            <div>
              <p className="section-kicker">Email delivery ledger / unlinked records</p>
              <h3 data-testid="studio-email-delivery-ledger-title">Every other dispatch<br /><em>still accounted for.</em></h3>
            </div>
            <span className="studio-contact-leads-count" data-testid="studio-email-delivery-ledger-count">{unlinkedDeliveries.length} {unlinkedDeliveries.length === 1 ? "unlinked delivery" : "unlinked deliveries"}</span>
          </div>
          <p className="studio-email-delivery-ledger-intro" data-testid="studio-email-delivery-ledger-intro">Purchase receipts, shipment updates, and newsletter acknowledgements stay visible here without storing the email body.</p>
          <div className="studio-email-delivery-list studio-email-delivery-ledger-list" data-testid="studio-email-delivery-ledger-list">
            {unlinkedDeliveries.map((delivery) => <DeliveryEntry key={delivery.id} delivery={delivery} testPrefix="studio-email-delivery-ledger-entry" onReview={reviewDelivery} onAddNote={promptPrivateNote} onShowFailed={showFailedDeliveries} notePromptKey={notePromptKey} reviewSavingKey={reviewSavingKey} reviewErrorKey={reviewErrorKey} reviewError={reviewError} />)}
          </div>
        </section>
      )}
      {!loading && !error && !deliveries.length && <div className="studio-email-delivery-ledger-empty" data-testid="studio-email-delivery-ledger-empty">No managed email deliveries have been recorded yet.</div>}
    </section>
  );
}
